/**
 * Who an alert, or a group of alerts, is assigned to — chosen BY NAME (owner ruling DEC-102,
 * `docs/adr/9040`).
 *
 * `GET /foundations/exceptions/assignees?country_key=[&rule_id=]` lists the people who may take
 * alerts in the market (and, with a rule, whose roles include its owner role), sorted by name,
 * authorised like the assignment act itself. The picker searches that list; «Assegna a me» is the
 * first option. A person the platform has no name for is shown by their id, in mono. A `truncated`
 * list is said to be partial and a typed id stays possible; so does a typed id while the read is not
 * pinned (`ALERT_ASSIGNEES_READ` is `null` until the re-pin — `src/api/pending/alert-groups.ts`) or
 * when no market is known, because the read requires one.
 *
 * Shared because two features assign: Management's group acts and the in-season decision bar.
 * It chooses a subject and nothing else; the write is the caller's.
 */

import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { callOperation } from '../api';
import { ALERT_ASSIGNEES_READ, type AlertAssignees, type AlertAssigneesRead } from '../api/pending/alert-groups';
import { Input } from '../primitives';
import { FOCUS_RING } from '../primitives/focus';

/** Options drawn at most; the search narrows the rest. */
export const ASSIGNEE_OPTIONS_SHOWN = 20;

export interface AssigneePickerProps {
  /** The market the act lands in. The read requires one: without it, a typed id. */
  readonly country: string | null;
  readonly ruleId?: string | null;
  /** The signed-in subject — «Assegna a me». `null`: not offered. */
  readonly me: string | null;
  /** The chosen subject, or `null`. */
  readonly value: string | null;
  /** The subject chosen, and the name the list gives them (`null`: none on record, or typed). */
  readonly onChange: (subject: string | null, name: string | null) => void;
  /** The read; the pinned constant by default — a stand-in in a test. */
  readonly read?: AlertAssigneesRead | null;
}

/** The people matching a search, by name or id, case-insensitively. Pure. */
export function matchAssignees(list: AlertAssignees['items'], query: string): AlertAssignees['items'] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') return list;
  return list.filter((item) => (item.displayName ?? '').toLocaleLowerCase().includes(needle)
    || item.subject.toLocaleLowerCase().includes(needle));
}

export function AssigneePicker({ country, ruleId = null, me, value, onChange, read = ALERT_ASSIGNEES_READ }: AssigneePickerProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [typed, setTyped] = useState('');
  const params = country === null ? null : { query: { country_key: country, ...(ruleId === null || ruleId === '' ? {} : { rule_id: ruleId }) } };
  const people = useQuery<AlertAssignees, Error>({
    queryKey: [read?.operationId ?? 'alert-assignees-unpinned', params, i18n.language],
    queryFn: ({ signal }) => callOperation(read!, params!, { language: i18n.language, signal }),
    enabled: read !== null && params !== null,
    staleTime: 60_000,
  });
  const listed = useMemo(() => people.data?.items ?? [], [people.data]);
  const matches = useMemo(() => matchAssignees(listed.filter((item) => item.subject !== me), query), [listed, me, query]);
  const shown = matches.slice(0, ASSIGNEE_OPTIONS_SHOWN);
  // A typed id is offered where the list cannot be complete: unpinned, no market, failed, or partial.
  const typing = read === null || params === null || people.isError || people.data?.truncated === true;
  const option = (subject: string, label: string, mono: boolean, name: string | null): React.JSX.Element => (
    <li key={subject} role="option" aria-selected={value === subject}>
      <button
        type="button"
        data-assignee-option={subject}
        onClick={() => { onChange(subject, name); setTyped(''); }}
        className={`w-full px-2 py-1 text-start text-[13px] ${value === subject ? 'bg-ink-primary text-surface-raised font-semibold' : 'text-ink-primary'} ${mono ? 'font-numeric' : ''} ${FOCUS_RING}`}
      >
        {label}
      </button>
    </li>
  );

  return (
    <div className="flex max-w-md flex-col gap-2" data-assignee-picker="">
      {read !== null && params !== null && (
        <Input labelKey="assignee.search" value={query} onChange={(event) => setQuery(event.target.value)} aria-controls={listId} />
      )}
      <ul id={listId} role="listbox" aria-label={t('assignee.list')} className="border-hairline flex max-h-[16rem] flex-col overflow-y-auto border">
        {me !== null && option(me, t('assignee.me'), false, null)}
        {shown.map((item) => (item.displayName !== null && item.displayName.trim() !== ''
          ? option(item.subject, item.displayName, false, item.displayName)
          : option(item.subject, item.subject, true, null)))}
      </ul>
      {people.isPending && read !== null && params !== null && <p className="text-caption text-ink-secondary">{t('state.loading')}</p>}
      {matches.length > shown.length && (
        <p className="text-caption text-ink-secondary">{t('assignee.more', { count: matches.length - shown.length })}</p>
      )}
      {people.data?.truncated === true && <p className="text-caption text-ink-secondary" data-assignee-partial="">{t('assignee.partial')}</p>}
      {people.isSuccess && listed.length === 0 && <p className="text-caption text-ink-secondary">{t('assignee.none')}</p>}
      {typing && (
        <Input
          labelKey="assignee.typed"
          helpKey={read === null ? 'assignee.typedUnpublished' : params === null ? 'assignee.typedNoMarket' : 'assignee.typedHelp'}
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
            const id = event.target.value.trim();
            onChange(id === '' ? null : id, null);
          }}
        />
      )}
    </div>
  );
}
